import { memo } from 'react';

import {
  faArrowsRotate,
  faBell,
  faBellRing,
  faChevronsDown,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { isSameDay } from 'date-fns';
import { useAtomValue } from 'jotai';

import {
  jumpToCurrentApptAtom,
  selectDateAtom,
} from '../../../modules/patientList/atoms';
import { usePreferences } from '../../../react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';

type PatientListActionsProps = {
  nextAppointmentIndex: number;
  hasNewAnalyses: boolean;
  isNextAppointmentInView: boolean;
  isRefreshRequested: boolean;
  onScrollNextAppointmentIntoView: () => void;
  onRefresh: () => void;
  onShowNewAnalyses: () => void;
};

export const PatientListActions = memo(({
  nextAppointmentIndex,
  hasNewAnalyses,
  isNextAppointmentInView,
  isRefreshRequested,
  onScrollNextAppointmentIntoView,
  onRefresh,
  onShowNewAnalyses,
}: PatientListActionsProps) => {
  const jumpToCurrentAppt = useAtomValue(jumpToCurrentApptAtom);
  const selectedDate = useAtomValue(selectDateAtom);
  const { data: preferences } = usePreferences();

  const today = new Date();

  const { trackEvent } = useTrackEvent();

  if (!selectedDate || (!!today && !isSameDay(selectedDate, today))) {
    return null;
  }

  const canJumpToCurrentAppointments =
    selectedDate &&
    !!today &&
    isSameDay(selectedDate, today) &&
    !isNextAppointmentInView &&
    nextAppointmentIndex >= 0 &&
    jumpToCurrentAppt === 'false';

  return (
    <>
      <Tooltip>
        <TooltipTrigger className="inline-block" asChild>
          <Button
            className="vai-w-9 vai-h-9"
            data-testid="jump-to-latest-button"
            size="icon"
            variant="ghostIcon"
            onClick={() => {
              trackEvent(mixpanelEvents.newBarJumpToLatest);
              onScrollNextAppointmentIntoView();
            }}
            disabled={!canJumpToCurrentAppointments}
          >
            <FontAwesomeIcon icon={faChevronsDown} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            <Trans>Jump to current appointments</Trans>
          </p>
        </TooltipContent>
      </Tooltip>

      {!preferences?.enableVoiceNotesStandalone && (
        <>
          <Tooltip>
            <TooltipTrigger className="inline-block" asChild>
              <Button
                data-testid="refresh-button"
                size="icon"
                variant="ghostIcon"
                onClick={() => {
                  trackEvent(mixpanelEvents.newBarCheckForAnalyses);
                  onRefresh();
                }}
                className={classNames('vai-w-9 vai-h-9', {
                  'vai-animate-spin': isRefreshRequested,
                })}
              >
                <FontAwesomeIcon icon={faArrowsRotate} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                <Trans>Check for new analyses</Trans>
              </p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger className="inline-block" asChild>
              <Button
                className="vai-w-9 vai-h-9"
                data-testid="new-analyses-button"
                size="icon"
                variant="ghostIcon"
                onClick={() => {
                  trackEvent(mixpanelEvents.newBarNewAnalyses);
                  onShowNewAnalyses();
                }}
                disabled={!hasNewAnalyses}
              >
                <FontAwesomeIcon icon={hasNewAnalyses ? faBellRing : faBell} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <Trans>New analyses available</Trans>
            </TooltipContent>
          </Tooltip>
        </>
      )}
    </>
  );
});

PatientListActions.displayName = 'PatientListActions';
